<template>
    <div class="flex-1 min-w-[200px]" :class="containerClass">
        <label v-if="label" class="block text-sm font-medium text-gray-700 mb-2">
            {{ label }}
        </label>
        <div class="relative" ref="autocompleteRef">
            <input
                v-model="searchQuery"
                @input="onSearchInput"
                @focus="isOpen = true"
                @keydown.down.prevent="navigate(1)"
                @keydown.up.prevent="navigate(-1)"
                @keydown.enter.prevent="selectHighlighted"
                @keydown.escape="isOpen = false"
                type="text"
                :placeholder="placeholder"
                class="w-full px-4 py-2.5 pr-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
            >
            <!-- Dropdown suggestions -->
            <Transition name="dropdown">
                <div
                    v-if="isOpen && filteredItems.length > 0"
                    class="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
                >
                    <div class="p-1">
                        <div
                            v-for="([id, name], index) in filteredItems"
                            :key="id"
                            @click="selectItem(id)"
                            @mouseenter="highlightedIndex = index"
                            class="flex items-center justify-between px-3 py-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                            :class="{
                                'bg-gray-50': highlightedIndex === index,
                                'opacity-50': selectedIds.includes(String(id))
                            }"
                        >
                            <span class="text-sm text-gray-700">{{ name }}</span>
                            <svg
                                v-if="selectedIds.includes(String(id))"
                                class="w-4 h-4 text-gray-600"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                            >
                                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                            </svg>
                        </div>
                    </div>
                </div>
            </Transition>
        </div>

        <!-- Badges -->
        <div v-if="showBadges && selectedIds.length > 0" class="flex flex-wrap gap-2 mt-2">
            <div
                v-for="id in selectedIds"
                :key="'badge-' + id"
                class="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-800 text-xs rounded-full transition-all duration-200 hover:bg-gray-200"
            >
                <span class="font-medium">{{ items[id] }}</span>
                <button
                    @click.prevent="removeItem(id)"
                    type="button"
                    class="ml-1.5 hover:bg-gray-300 rounded-full p-0.5 transition-colors"
                >
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>

        <!-- Hidden inputs pour le formulaire -->
        <input
            v-for="id in selectedIds"
            :key="'hidden-' + id"
            type="hidden"
            :name="fieldName + '[]'"
            :value="id"
        >
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
    items: {
        type: Object,
        default: () => ({})
    },
    // Valeurs initialement sélectionnées
    modelValue: {
        type: Array,
        default: () => []
    },
    // Nom du champ pour le formulaire
    fieldName: {
        type: String,
        required: true
    },
    // Label du champ
    label: {
        type: String,
        default: ''
    },
    // Placeholder
    placeholder: {
        type: String,
        default: 'Rechercher...'
    },
    // Afficher les badges
    showBadges: {
        type: Boolean,
        default: true
    },
    containerClass: {
        type: [String, Array, Object],
        default: ''
    }
})

const emit = defineEmits(['update:modelValue'])

// États
const selectedIds = ref([...props.modelValue].map(String))
const searchQuery = ref('')
const isOpen = ref(false)
const highlightedIndex = ref(0)
const autocompleteRef = ref(null)

// Synchroniser avec le v-model
watch(() => props.modelValue, (newVal) => {
    selectedIds.value = [...newVal].map(String)
}, { deep: true })

// Émettre les changements
watch(selectedIds, (newVal) => {
    emit('update:modelValue', newVal)
}, { deep: true })

// Filtrage
const filteredItems = computed(() => {
    const entries = Object.entries(props.items)

    if (!searchQuery.value.trim()) {
        return entries
    }

    const query = searchQuery.value.toLowerCase()
    return entries.filter(([id, name]) =>
        name.toLowerCase().includes(query)
    )
})

// Fonctions
const onSearchInput = () => {
    isOpen.value = true
    highlightedIndex.value = 0
}

const navigate = (direction) => {
    if (filteredItems.value.length === 0) return
    highlightedIndex.value += direction
    if (highlightedIndex.value < 0) {
        highlightedIndex.value = filteredItems.value.length - 1
    } else if (highlightedIndex.value >= filteredItems.value.length) {
        highlightedIndex.value = 0
    }
}

const selectHighlighted = () => {
    if (filteredItems.value.length > 0 && highlightedIndex.value >= 0) {
        const [id] = filteredItems.value[highlightedIndex.value]
        selectItem(id)
    }
}

const selectItem = (id) => {
    const stringId = String(id)
    if (!selectedIds.value.includes(stringId)) {
        selectedIds.value.push(stringId)
    }
    searchQuery.value = ''
    isOpen.value = false
    highlightedIndex.value = 0
}

const removeItem = (id) => {
    selectedIds.value = selectedIds.value.filter(selectedId => selectedId !== String(id))
}

const handleClickOutside = (event) => {
    if (autocompleteRef.value && !autocompleteRef.value.contains(event.target)) {
        isOpen.value = false
    }
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
})

// Exposer pour réinitialiser depuis l'extérieur
defineExpose({
    reset: () => {
        selectedIds.value = []
    }
})
</script>

<style scoped>
.dropdown-enter-active, .dropdown-leave-active {
    transition: all 0.2s ease;
}

.dropdown-enter-from, .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

.dropdown-enter-to, .dropdown-leave-from {
    opacity: 1;
    transform: translateY(0);
}
</style>
